import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model/MediaObject';
import { ResizeMode, Video } from 'expo-av';
import { ComponentType, ReactNode, memo, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { ImageStyle } from 'react-native-fast-image';
import { useTheme } from 'styled-components';

export const MediaCoverView = memo(
  ({
    media,
    index,
    style,
    imageStyle,
    containerStyle,
    placeholderStyle,
    noPlaceholder,
    playIconSize,
    playIconStyle,
    playIconColor,
    preferOriginalImage,
    activityIndicatorSize,
    children,
    onPress,
    TouchableComponent,
  }: {
    media: MediaObject;
    index?: number;
    preferOriginalImage?: boolean;
    noPlaceholder?: boolean;
    style?: StyleProp<ViewStyle>;
    imageStyle?: StyleProp<ImageStyle>;
    containerStyle?: StyleProp<ViewStyle>;
    placeholderStyle?: StyleProp<ViewStyle>;
    playIconSize?: number;
    playIconStyle?: StyleProp<ViewStyle>;
    playIconColor?: string;
    activityIndicatorSize?: number | 'small' | 'large';
    TouchableComponent?: ComponentType;
    onPress?: (media: MediaObject, index: number | undefined) => void;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const Component = TouchableComponent ?? TouchableWithoutFeedback;
    const theme = useTheme();
    const [loaded, setLoaded] = useState(false);

    const onLoad = useCallback(() => {
      setLoaded(true);
    }, []);

    const coverUrl =
      media.type === 'video'
        ? media.coverUrl
        : !preferOriginalImage && media.coverUrl
        ? media.coverUrl
        : media.url;

    const handlePress = useCallback(() => {
      onPress?.(media, index);
    }, [index, media, onPress]);

    return (
      <Component onPress={handlePress} style={[{ flex: 1 }, containerStyle]}>
        {/* We need wrap it with a View otherwise the touch will not
           register unless image loaded  */}
        <View
          css={`
            flex: 1;
            width: 100%;
          `}
          style={style}
        >
          {coverUrl ? (
            <AdvancedImage
              resizeMode={'cover'}
              uri={coverUrl}
              onLoad={onLoad}
              css={`
                flex: 1;
                width: 100%;
              `}
              style={imageStyle}
            />
          ) : media.type === 'video' ? (
            <Video
              source={{ uri: media.url }}
              shouldPlay={false}
              onLoad={onLoad}
              resizeMode={ResizeMode.COVER}
              videoStyle={{
                width: '100%',
                height: '100%',
              }}
              css={`
                flex: 1;
                width: 100%;
              `}
            />
          ) : null}
          {children != null ? <>{children}</> : null}
          {media.type === 'video' && (
            <TouchableOpacity
              style={StyleSheet.absoluteFill}
              onPress={handlePress}
              css={`
                background-color: rgba(0, 0, 0, 0.25);
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon
                name="play"
                size={playIconSize ?? rpx(32)}
                color={playIconColor ?? 'white'}
                style={playIconStyle}
              />
            </TouchableOpacity>
          )}
          {!loaded && !noPlaceholder && (
            <Center
              css={`
                background-color: ${theme.components.placeholder.background};
              `}
              style={[StyleSheet.absoluteFill, placeholderStyle]}
            >
              <ActivityIndicator size={activityIndicatorSize ?? 'small'} />
            </Center>
          )}
        </View>
      </Component>
    );
  },
);
